"use client";

import { useTranslations } from "next-intl";

const CORE_PARAMS = [
	{ label: "coreParamsDimensionalAccuracy", value: "coreParamsDimensionalAccuracyValue" },
	{ label: "coreParamsSurfaceFinish", value: "coreParamsSurfaceFinishValue" },
	{ label: "coreParamsMinWallThickness", value: "coreParamsMinWallThicknessValue" },
	{ label: "coreParamsFillingPressure", value: "coreParamsFillingPressureValue" },
	{ label: "coreParamsFillingSpeed", value: "coreParamsFillingSpeedValue" },
	{ label: "coreParamsCastingDensity", value: "coreParamsCastingDensityValue" },
	{ label: "coreParamsWeightRange", value: "coreParamsWeightRangeValue" },
	{ label: "coreParamsBatchCapacity", value: "coreParamsBatchCapacityValue" },
] as const;

const TABLE_HEADERS = [
	"coreParamsTableHeaderMaterial",
	"coreParamsTableHeaderMeltingTemp",
	"coreParamsTableHeaderTensileStrength",
	"coreParamsTableHeaderHardness",
	"coreParamsTableHeaderProcess",
	"coreParamsTableHeaderApplication",
] as const;

const ALLOY_ROWS = [
	{
		name: "coreParamsAlloy1Name",
		meltingTemp: "coreParamsAlloy1MeltingTemp",
		tensileStrength: "coreParamsAlloy1TensileStrength",
		hardness: "coreParamsAlloy1Hardness",
		process: "coreParamsAlloy1Process",
		application: "coreParamsAlloy1Application",
	},
	{
		name: "coreParamsAlloy2Name",
		meltingTemp: "coreParamsAlloy2MeltingTemp",
		tensileStrength: "coreParamsAlloy2TensileStrength",
		hardness: "coreParamsAlloy2Hardness",
		process: "coreParamsAlloy2Process",
		application: "coreParamsAlloy2Application",
	},
	{
		name: "coreParamsAlloy3Name",
		meltingTemp: "coreParamsAlloy3MeltingTemp",
		tensileStrength: "coreParamsAlloy3TensileStrength",
		hardness: "coreParamsAlloy3Hardness",
		process: "coreParamsAlloy3Process",
		application: "coreParamsAlloy3Application",
	},
	{
		name: "coreParamsAlloy4Name",
		meltingTemp: "coreParamsAlloy4MeltingTemp",
		tensileStrength: "coreParamsAlloy4TensileStrength",
		hardness: "coreParamsAlloy4Hardness",
		process: "coreParamsAlloy4Process",
		application: "coreParamsAlloy4Application",
	},
	{
		name: "coreParamsAlloy5Name",
		meltingTemp: "coreParamsAlloy5MeltingTemp",
		tensileStrength: "coreParamsAlloy5TensileStrength",
		hardness: "coreParamsAlloy5Hardness",
		process: "coreParamsAlloy5Process",
		application: "coreParamsAlloy5Application",
	},
] as const;

const PROCESS_ROWS = [
	{ label: "coreParamsProcessRowFillingPressure", suffix: "FillingPressure" },
	{ label: "coreParamsProcessRowPressureControl", suffix: "PressureControl" },
	{ label: "coreParamsProcessRowCastingDensity", suffix: "CastingDensity" },
	{ label: "coreParamsProcessRowDimensionalAccuracy", suffix: "DimensionalAccuracy" },
	{ label: "coreParamsProcessRowPrototypeLeadTime", suffix: "PrototypeLeadTime" },
] as const;

const PROCESS_CARDS = [
	{ title: "coreParamsProcess1Title", prefix: "coreParamsProcess1" },
	{ title: "coreParamsProcess2Title", prefix: "coreParamsProcess2" },
	{ title: "coreParamsProcess3Title", prefix: "coreParamsProcess3" },
] as const;

export default function CoreTechnicalParameters() {
	const t = useTranslations("new-low-pressure-casting");

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("coreParamsTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("coreParamsDesc")}</p>
				</header>

				{/* General Core Parameters */}
				<h3 className="mb-6 text-center text-lg font-bold text-black sm:mb-8 sm:text-xl md:text-2xl">
					{t("coreParamsSectionTitle")}
				</h3>
				<div className="mb-8 grid grid-cols-2 gap-x-4 gap-y-6 sm:mb-10 md:mb-12 md:grid-cols-4 md:gap-x-6 lg:mb-14">
					{CORE_PARAMS.map((param) => (
						<div key={param.label} className="text-center">
							<p className="text-xs text-gray-900 sm:text-sm">{t(param.label)}</p>
							<p className="mt-1 text-sm font-bold text-black sm:text-base md:text-lg">{t(param.value)}</p>
						</div>
					))}
				</div>

				{/* Materials Table */}
				<h3 className="mb-6 text-center text-lg font-bold text-black sm:mb-8 sm:text-xl md:text-2xl">
					{t("coreParamsMaterialsSectionTitle")}
				</h3>
				<div className="mb-8 overflow-x-auto sm:mb-10 md:mb-12 lg:mb-14">
					<table className="w-full min-w-[800px] border-collapse">
						<thead>
							<tr className="bg-[#64a525] text-white">
								{TABLE_HEADERS.map((header) => (
									<th
										key={header}
										className="px-4 py-3 text-left text-xs font-bold sm:text-sm md:px-5 md:py-4"
									>
										{t(header)}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{ALLOY_ROWS.map((row) => (
								<tr key={row.name} className="border-b border-gray-200 bg-white">
									<td className="px-4 py-4 text-xs font-bold text-black sm:text-sm md:px-5 md:py-5">
										{t(row.name)}
									</td>
									<td className="px-4 py-4 text-xs text-black sm:text-sm md:px-5 md:py-5">
										{t(row.meltingTemp)}
									</td>
									<td className="px-4 py-4 text-xs text-black sm:text-sm md:px-5 md:py-5">
										{t(row.tensileStrength)}
									</td>
									<td className="px-4 py-4 text-xs text-black sm:text-sm md:px-5 md:py-5">
										{t(row.hardness)}
									</td>
									<td className="px-4 py-4 text-xs text-black sm:text-sm md:px-5 md:py-5">
										{t(row.process)}
									</td>
									<td className="px-4 py-4 text-xs text-black sm:text-sm md:px-5 md:py-5">
										{t(row.application)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				{/* Three Process Cards */}
				<h3 className="mb-6 text-center text-lg font-bold text-black sm:mb-8 sm:text-xl md:text-2xl">
					{t("coreParamsProcessSectionTitle")}
				</h3>
				<div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-3 md:gap-6">
					{PROCESS_CARDS.map((card) => (
						<div key={card.title} className="rounded-md bg-[#f6ffde] p-5 sm:p-6 md:p-7">
							<h4 className="mb-4 text-center text-base font-bold text-themePrimary400 sm:mb-5 sm:text-lg md:mb-6">
								{t(card.title)}
							</h4>
							<dl className="space-y-3 sm:space-y-4">
								{PROCESS_ROWS.map((row) => (
									<div
										key={row.suffix}
										className="flex items-center justify-between gap-3 border-b border-[#cbccc5] pb-2 last:border-b-0 last:pb-0"
									>
										<dt className="text-xs text-gray-900 sm:text-sm">{t(row.label)}</dt>
										<dd className="text-xs font-bold text-black sm:text-sm">
											{t(`${card.prefix}${row.suffix}` as Parameters<typeof t>[0])}
										</dd>
									</div>
								))}
							</dl>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
